import { EditCallLog, GetCallLog, RegisterCallLog } from "@/dtos/CallLog.dto";
import { StateObject } from "@/dtos/State.dto";
import CallLog from "@/services/call-log";
import {
  CLEAR_CALL_LOG_ISLOADING,
  DELETE_CALL_LOG,
  GET_CALL_LOGS,
  LOG_CALL_LOG_DETAILS,
  REGISTER_CALL_LOG,
} from "@/store/action-types";
import { SET_CALL_LOG, SET_CALL_LOGS, SET_ERRORMSG } from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const callLog: Module<any, {}> = {
  state: {
    callLog: {
      data: null,
      isLoading: false,
    },
    callLogs: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    callLog: (state: { callLog: any }) => state.callLog,
    callLogs: (state: { callLogs: any }) => state.callLogs,
  },
  mutations: {
    [SET_CALL_LOG]: (
      state: { callLog: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.callLog.data = payload.data;
      state.callLog.isLoading = payload.isLoading;
    },
    [SET_CALL_LOGS]: (
      state: { callLogs: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.callLogs.data = payload.data;
      if (payload.metaData != null) state.callLogs.metaData = payload.metaData;
      state.callLogs.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_CALL_LOG]: async ({ commit }: any, payload: any) => {
      commit(SET_CALL_LOG, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await CallLog.registerCallLog(payload as RegisterCallLog);

        if (result) {
          if (result.data.status) {
            commit(SET_CALL_LOG, { data: result.data.data, isLoading: false });
            // Return the actual call log data (including parent CallSid from backend)
            return result.data.data;
          }
          commit(SET_CALL_LOG, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CALL_LOG, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CALL_LOG, { data: null, isLoading: false });
        commit(
          SET_ERRORMSG,
          error.response?.data?.message || "Failed to register call log. Please try again."
        );
        return false;
      }
    },
    [GET_CALL_LOGS]: async ({ commit }: any, payload: any) => {
      commit(SET_CALL_LOGS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await CallLog.getCallLogs(payload as GetCallLog);

        if (result) {
          if (result.data.status) {
            commit(SET_CALL_LOGS, {
              data: result.data.data,
              metaData: result.data.metaData,
              isLoading: false,
            });
            return true;
          }
          commit(SET_CALL_LOGS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CALL_LOGS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CALL_LOGS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(
            SET_ERRORMSG,
            "Request timed out. The server is taking too long to respond. Please try again later."
          );
        } else if (error.response?.status === 500) {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message ||
              "Server error occurred while loading call logs. Please try again later."
          );
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Call logs not found. Please contact support.");
        } else if (!error.response) {
          commit(
            SET_ERRORMSG,
            "Network error. Please check your internet connection and try again."
          );
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load call logs. Please try again."
          );
        }

        return false;
      }
    },
    [LOG_CALL_LOG_DETAILS]: async ({ commit }: any, payload: any) => {
      commit(SET_CALL_LOG, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await CallLog.logCallLogDetails(payload as EditCallLog);

        if (result) {
          if (result.data.status) {
            commit(SET_CALL_LOG, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_CALL_LOG, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CALL_LOG, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CALL_LOG, { data: null, isLoading: false });
        commit(
          SET_ERRORMSG,
          error.response?.data?.message || "Failed to update call log. Please try again."
        );
        return false;
      }
    },
    [DELETE_CALL_LOG]: async ({ commit }: any, payload: string) => {
      commit(SET_CALL_LOG, { data: { id: payload }, isLoading: true });

      try {
        const result: AxiosResponse = await CallLog.deleteCallLog(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_CALL_LOG, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_CALL_LOG, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CALL_LOG, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CALL_LOG, { data: null, isLoading: false });
        commit(
          SET_ERRORMSG,
          error.response?.data?.message || "Failed to delete call log. Please try again."
        );
        return false;
      }
    },
    [CLEAR_CALL_LOG_ISLOADING]: ({ commit }) => {
      commit(SET_CALL_LOG, { isLoading: false });
      commit(SET_CALL_LOGS, { isLoading: false });
    },
  },
};

export default callLog;
